import { FC, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import SunshineCard from 'components/_core/SunshineCard';

import type { DetailedTransactionReceipt } from '../../hooks/useDetailedTransactionQuery';
import AddVat from './components/AddVat';
import DisplayVat from './components/DisplayVat';
import { FormSchema, formSchema, getDefaultRows } from './form';
import { VatMode } from './types';

type VatAmountProps = {
  receipt: DetailedTransactionReceipt;
  transactionTotal: number;
} & React.ComponentProps<typeof SunshineCard>;

const VatAmount: FC<VatAmountProps> = ({
  receipt,
  transactionTotal,
  ...sunshineCardProps
}) => {
  const vatAmount = receipt?.vatAmount;
  const vatAmountItems = useMemo(
    () => receipt?.vatAmount?.vatAmountItems ?? [],
    [receipt],
  );
  const hasVatAmount = !!vatAmount && vatAmount.vatAmountItems.length > 0;
  const defaultValues = useMemo(
    () => ({ rows: getDefaultRows(vatAmountItems) }),
    [vatAmountItems],
  );

  const form = useForm<FormSchema>({
    defaultValues,
    mode: 'onChange',
    resolver: zodResolver(formSchema),
  });

  const [innerMode, setMode] = useState<VatMode>('display');

  // exit edition when transaction changes
  useEffect(() => {
    form.reset(defaultValues);
    setMode(hasVatAmount ? 'display' : 'creation');
  }, [hasVatAmount, form, defaultValues]);

  return innerMode === 'display' ? (
    <DisplayVat
      onEdit={() => setMode('edition')}
      vatAmountItems={vatAmountItems}
      {...sunshineCardProps}
    />
  ) : (
    <FormProvider {...form}>
      <AddVat
        mode={innerMode}
        onFormClosed={() => setMode('display')}
        receipt={receipt}
        transactionTotal={transactionTotal}
        {...sunshineCardProps}
      />
    </FormProvider>
  );
};

export default VatAmount;
